/**
 * Utility functions for formatting metrics consistently across the application
 */

/**
 * Ensure value is a number
 * 
 * @param {any} value - Value to convert to number
 * @returns {number} The value as a number, or 0 if not convertible
 */
const ensureNumber = (value) => {
  if (value === null || value === undefined) return 0;
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  return isNaN(num) ? 0 : num;
};

/**
 * Format value as percentage with specified precision
 * 
 * @param {number} value - The value to format as percentage
 * @param {number} precision - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, precision = 1) => {
  if (value === null || value === undefined) return 'N/A';
  
  // Convert value to number if it isn't already
  value = ensureNumber(value);
  
  // Don't multiply by 100, as the backend already returns percentages
  const formattedValue = value.toFixed(precision);
  
  return `${formattedValue}%`;
};

/**
 * Format value as currency
 * 
 * @param {number} value - The value to format as currency
 * @param {number} precision - Number of decimal places (default: 0)
 * @param {string} currencySymbol - Currency symbol to use (default: '$')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, precision = 0, currencySymbol = '$') => {
  if (value === null || value === undefined) return 'N/A';
  
  // Convert value to number if it isn't already
  value = ensureNumber(value);
  
  // Format with commas for thousands separators
  const formatted = value.toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision
  });
  
  return value >= 0 
    ? `${currencySymbol}${formatted}` 
    : `(${currencySymbol}${Math.abs(value).toLocaleString('en-US', {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision
      })})`;
};

/**
 * Format large currency values in thousands (k) or millions (M)
 * 
 * @param {number} value - The value to format
 * @param {number} precision - Number of decimal places (default: 1)
 * @param {string} currencySymbol - Currency symbol to use (default: '$')
 * @returns {string} Formatted currency string with k or M suffix
 */
export const formatLargeCurrency = (value, precision = 1, currencySymbol = '$') => {
  if (value === null || value === undefined) return 'N/A';
  
  // Convert value to number if it isn't already
  value = ensureNumber(value);
  
  const absValue = Math.abs(value);
  const prefix = value >= 0 ? currencySymbol : `(${currencySymbol}`;
  const suffix = value >= 0 ? '' : ')';
  
  if (absValue >= 1000000) {
    return `${prefix}${(absValue / 1000000).toFixed(precision)}M${suffix}`;
  } else if (absValue >= 1000) {
    return `${prefix}${(absValue / 1000).toFixed(precision)}k${suffix}`;
  } else {
    return `${prefix}${absValue.toLocaleString('en-US', {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision
    })}${suffix}`;
  }
};

/**
 * Format a ratio value (e.g., profit factor)
 * 
 * @param {number} value - The ratio value to format
 * @param {number} precision - Number of decimal places (default: 2)
 * @returns {string} Formatted ratio
 */
export const formatRatio = (value, precision = 2) => {
  if (value === null || value === undefined) return 'N/A';
  
  // Convert value to number if it isn't already
  value = ensureNumber(value);
  
  return value.toFixed(precision);
};

/**
 * Format win/loss data to display only the win count
 * 
 * @param {Object} winLossData - Object containing win/loss counts
 * @param {boolean} verbose - Whether to use verbose format (default: false)
 * @returns {string} Formatted win count
 */
export const formatWinLoss = (winLossData, verbose = false) => {
  if (!winLossData) return 'N/A';
  
  // Directly access win_months property if available
  const wins = winLossData.win_months !== undefined ? winLossData.win_months : 
               (winLossData.wins !== undefined ? winLossData.wins : undefined);
  
  if (wins === undefined) return 'N/A';
  
  return verbose ? `${wins} wins` : String(wins);
};

/**
 * Determine appropriate color for a value based on whether it's positive/negative
 * 
 * @param {number} value - The numeric value
 * @param {Object} options - Options for color determination
 * @param {string} options.positiveColor - Color for positive values (default: 'success.main')
 * @param {string} options.negativeColor - Color for negative values (default: 'error.main')
 * @param {string} options.neutralColor - Color for zero or null values (default: 'text.primary')
 * @returns {string} The appropriate color
 */
export const getValueColor = (value, options = {}) => {
  const { 
    positiveColor = 'success.dark', 
    negativeColor = 'error.main', 
    neutralColor = 'text.primary' 
  } = options;
  
  if (value === null || value === undefined) return neutralColor;
  
  // Convert value to number if it isn't already
  value = ensureNumber(value);
  
  if (value === 0) return neutralColor;
  return value > 0 ? positiveColor : negativeColor;
};

/**
 * Format metric value based on metric type
 * 
 * @param {string} metricType - Type of metric (percentage, currency, ratio, winLoss)
 * @param {any} value - The value to format
 * @param {Object} options - Additional formatting options
 * @returns {string} Formatted metric value
 */
export const formatMetricValue = (metricType, value, options = {}) => {
  if (value === null || value === undefined) return 'N/A';
  
  switch (metricType) {
    case 'percentage':
      return formatPercentage(value, options.precision);
    case 'currency':
      return formatCurrency(value, options.precision);
    case 'largeCurrency':
      return formatLargeCurrency(value, options.precision);
    case 'ratio':
      return formatRatio(value, options.precision);
    case 'winLoss':
      return formatWinLoss(value, options.verbose);
    default:
      return String(value);
  }
}; 